import {
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  Autocomplete,
  Pagination,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { OrderContext } from "../contexts/OrderContext";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import UniversalDataTable from "./UniversalDataTable";
import { useLocation, useNavigate } from "react-router-dom";

function DesignCatalogue() {
  const navigate = useNavigate();
  const { storeData } = useContext(OrderContext);

  const storeID = JSON.parse(localStorage.getItem("selectedMfr")).id;
  const { designs = [], karigars = [] } = storeData;

  const [formKey, setFormKey] = useState(Date.now());
  const [isCreating, setIsCreating] = useState(false);
  const [existingDesignsArr, setExistingDesignsArr] = useState([]);
  const [filteredDesignsArr, setFilteredDesignsArr] = useState([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [designsPerPage, setDesignsPerPage] = useState(10); // Number of orders per page
  const [showSuccessBackdrop, setShowSuccessBackdrop] = useState(false);
  const [showErrorBackdrop, setShowErrorBackdrop] = useState(false);

  // Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const location = useLocation();

  // UI message for design update
  useEffect(() => {
    if (location.state?.editSuccess) {
      setSnackbarMessage(
        `Design ${location.state.editedTagNo} updated successfully.`
      );
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

      // Prevent snackbar from showing on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Getting existing designs of a store
  const existingDesignsFetcher = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/fetchorder/getStockByStoreG/${storeID}`
    );
    setExistingDesignsArr(response.data);
  };
  useEffect(() => {
    existingDesignsFetcher();
  }, [showSuccessBackdrop]);

  // Design catalogue data object
  const [designCatalogueData, setdesignCatalogueData] = useState({
    designNo: "",
    designAlias: "",
    item: null,
    design: null,
    grossWt: "",
    netWt: "",
    karigar: "",
    noOfDays: "",
    size: "",
    designDate: new Date().toISOString().split("T")[0],
    collection: "",
    toleranceFrom: "",
    toleranceTo: "",
    status: "",
    designDescription: "",
    files: [],
    store_id: storeID,
  });

  const filteredDesigns = designCatalogueData.item
    ? designs.filter(
        (design) => design.itemtype_id === designCatalogueData.item.id
      )
    : [];

  const dataToPaginate = isFilterActive
    ? filteredDesignsArr
    : existingDesignsArr;

  const indexOfLastDesign = currentPage * designsPerPage;
  const indexOfFirstDesign = indexOfLastDesign - designsPerPage;

  const currentDesigns = dataToPaginate.slice(
    indexOfFirstDesign,
    indexOfLastDesign
  );

  // Fields for the form on UI
  const fields = [
    // Row 1
    { label: "Design No", key: "designNo", type: "text" },
    { label: "Design Alias", key: "designAlias", type: "text" },
    {
      label: "Item",
      key: "item",
      type: "select",
      options: storeData?.itemtypes?.map((item) => item.name) || [],
    },

    // Row 2
    {
      label: "Design",
      key: "design",
      type: "select",
      options: storeData?.designs?.map((design) => design.name) || [],
    },
    { label: "Size", key: "size", type: "number", step: 1 },
    {
      label: "Collection",
      key: "collection",
      type: "text",
    },

    // Row 3
    { label: "Gross Wt", key: "grossWt", type: "number", step: 0.001 },
    { label: "Net Wt", key: "netWt", type: "number", step: 0.001 },
    { label: "No of Days", key: "noOfDays", type: "number", step: 1 },

    // Row 4
    {
      label: "Tolerance/WT From",
      key: "toleranceFrom",
      type: "number",
      step: 0.001,
    },
    {
      label: "Tolerance/WT To",
      key: "toleranceTo",
      type: "number",
      step: 0.001,
    },
    {
      label: "Design Date",
      key: "designDate",
      type: "date",
    },

    // Row 5
    {
      label: "Karigar",
      key: "karigar",
      type: "select",
      options: storeData?.karigars?.map((karigar) => karigar.name) || [],
    },
    {
      label: "Status",
      key: "status",
      type: "select",
      options: [
        { label: "Active", value: "A" },
        { label: "Inactive", value: "I" },
      ],
    },
  ];

  // Restrict decimal number inputs
  const getDecimalPlaces = (step) => {
    const stepStr = step.toString();
    return stepStr.includes(".") ? stepStr.split(".")[1].length : 0;
  };

  // Decimal change handler
  const handleDecimalChange = (key, step) => (e) => {
    const value = e.target.value;
    const decimalPlaces = getDecimalPlaces(step);

    // Allow only valid decimal format with up to N digits
    const regex = new RegExp(`^\\d*(\\.\\d{0,${decimalPlaces}})?$`);

    if (value === "" || regex.test(value)) {
      setdesignCatalogueData((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  // Change handler
  const handleChange = (key) => (event) => {
    const value = ["designNo", "designAlias"].includes(key)
      ? event.target.value.toUpperCase()
      : event.target.value;

    setdesignCatalogueData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // File selection on drop handler
  const onDrop = (acceptedFiles) => {
    const newFiles = acceptedFiles.filter((file) =>
      file.type.startsWith("image/")
    );

    setdesignCatalogueData((prevState) => {
      const totalFiles = prevState.files.length + newFiles.length;
      const filesToAdd =
        totalFiles <= 4
          ? newFiles
          : newFiles.slice(0, 4 - prevState.files.length);

      return {
        ...prevState,
        files: [...prevState.files, ...filesToAdd],
      };
    });
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*, video/*,application/pdf", // Accept both images and videos
    multiple: true, // Allow multiple files
  });

  // Remove uploaded file handler
  const handleRemoveFile = (indexToRemove) => {
    setdesignCatalogueData((prev) => ({
      ...prev,
      files: prev.files.filter((_, index) => index !== indexToRemove),
    }));
  };

  // Form validation before submitting
  const isFormValid = () => {
    const requiredFields = [
      "designNo",
      "design",
      "grossWt",
      "netWt",
      "karigar",
      "status",
    ];

    if (!designCatalogueData.item) return false;

    if (!designCatalogueData.item || !designCatalogueData.design) return false;

    for (let field of requiredFields) {
      if (!designCatalogueData[field]) return false;
    }

    return (
      designCatalogueData.files.length > 0 &&
      designCatalogueData.files.length <= 4
    );
  };

  // Submit handler
  const handleSubmit = async () => {
    try {
      if (isCreating) return;
      setIsCreating(true);

      const payloadData = new FormData();

      for (let key in designCatalogueData) {
        if (key === "item" && designCatalogueData.item?.name) {
          payloadData.append("item", designCatalogueData.item.id);
        } else if (key === "design" && designCatalogueData.design?.name) {
          payloadData.append("design", designCatalogueData.design.id);
        } else if (
          key !== "files" &&
          key !== "item" &&
          key !== "design" &&
          designCatalogueData[key] !== null
        ) {
          payloadData.append(key, designCatalogueData[key]);
        }
      }

      // Append files
      designCatalogueData.files.forEach((file, index) => {
        payloadData.append(`image${index + 1}`, file);
      });

      // Sending the design catalogue object as payload
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/fetchorder/catalogueinsert`,
        payloadData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) setShowSuccessBackdrop(true);

      // Optional: simulate delay for smoother UI (not mandatory)
      await new Promise((res) => setTimeout(res, 200));

      // Safely update data only when everything is ready
      // setExistingDesignsArr((prev) => [...prev, payloadData]);

      // Reset form
      setdesignCatalogueData({
        designNo: "",
        designAlias: "",
        item: null,
        design: null,
        grossWt: "",
        netWt: "",
        karigar: "",
        noOfDays: "",
        size: "",
        designDate: new Date().toISOString().split("T")[0],
        collection: "",
        toleranceFrom: "",
        toleranceTo: "",
        status: "",
        designDescription: "",
        files: [],
        store_id: storeID,
      });

      setFormKey(Date.now());

      // Scroll to top
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } catch (error) {
      setShowErrorBackdrop(true);
      console.error("Upload failed:", error);
    } finally {
      setIsCreating(false);
    }
  };

  // Enable filter button based on the fields filled
  const isFilterEnabled = () => {
    const keysToCheck = [
      "designNo",
      "designAlias",
      "item",
      "design",
      "grossWt",
      "netWt",
      "karigar",
      "status",
      "size",
      "collection",
      "noOfDays",
      "toleranceFrom",
      "toleranceTo",
    ];

    // Check all non-date fields
    const isAnyFieldFilled = keysToCheck.some((key) => {
      const value = designCatalogueData[key];
      return value !== "" && value !== null;
    });

    // Explicitly check if user has **manually changed date**
    const today = new Date().toISOString().split("T")[0];
    const isDateManuallySet = designCatalogueData.designDate !== today;

    return isAnyFieldFilled || isDateManuallySet;
  };

  // Filter handler
  const handleFilter = () => {
    const filtered = existingDesignsArr.filter((design) => {
      const matches = [];

      // Design no
      if (designCatalogueData.designNo) {
        matches.push(
          design.tagno_alpha
            ?.toLowerCase()
            .includes(designCatalogueData.designNo.toLowerCase())
        );
      }

      // Design alias
      if (designCatalogueData.designAlias) {
        matches.push(
          design.certificateno
            ?.toLowerCase()
            .includes(designCatalogueData.designAlias.toLowerCase())
        );
      }

      // Item
      if (designCatalogueData.item) {
        matches.push(design.itemtype === designCatalogueData.item.id);
      }

      // Design type
      if (designCatalogueData.design) {
        matches.push(design.design === designCatalogueData.design.id);
      }

      // Size
      if (designCatalogueData.size) {
        matches.push(
          design.s_size_len
            ?.toLowerCase()
            .includes(designCatalogueData.size.toLowerCase())
        );
      }

      // Collection
      if (designCatalogueData.collection) {
        matches.push(
          design.mfn_code
            ?.toLowerCase()
            .includes(designCatalogueData.collection.toLowerCase())
        );
      }

      // Gross weight
      if (designCatalogueData.grossWt) {
        matches.push(
          String(design.gross) === String(designCatalogueData.grossWt)
        );
      }

      // Net weight
      if (designCatalogueData.netWt) {
        matches.push(
          String(design.netwt) === String(designCatalogueData.netWt)
        );
      }

      // Number of days
      if (designCatalogueData.noOfDays) {
        matches.push(
          String(design.pcs) === String(designCatalogueData.noOfDays)
        );
      }

      // Tolerance from
      if (designCatalogueData.toleranceFrom) {
        matches.push(
          String(design.tear_wt) === String(designCatalogueData.toleranceFrom)
        );
      }

      // Tolerance to
      if (designCatalogueData.toleranceTo) {
        matches.push(
          String(design.pouch_tear_wt) ===
            String(designCatalogueData.toleranceTo)
        );
      }

      // Only check design date if user has changed it
      if (
        designCatalogueData.designDate &&
        designCatalogueData.designDate !==
          new Date().toISOString().split("T")[0]
      ) {
        const formattedEntryDate = new Date(design.entrydate)
          .toISOString()
          .split("T")[0];
        matches.push(formattedEntryDate === designCatalogueData.designDate);
      }

      // Karigar
      if (designCatalogueData.karigar) {
        matches.push(
          String(design.supplier) === String(designCatalogueData.karigar)
        );
      }

      // Status
      if (designCatalogueData.status) {
        matches.push(design.status === designCatalogueData.status);
      }

      return matches.every(Boolean);
    });

    setFilteredDesignsArr(filtered);
    setIsFilterActive(true);
    setCurrentPage(1);
  };

  // Clear filter handler
  const handleClearFilters = () => {
    setdesignCatalogueData((prev) => ({
      ...prev,
      designNo: "",
      designAlias: "",
      item: null,
      design: null,
      grossWt: "",
      netWt: "",
      karigar: "",
      noOfDays: "",
      size: "",
      designDate: new Date().toISOString().split("T")[0],
      collection: "",
      toleranceFrom: "",
      toleranceTo: "",
      status: "",
      designDescription: "",
      // Keep files and store_id intact
      files: prev.files,
      store_id: prev.store_id,
    }));

    setFilteredDesignsArr([]); // Show all
    setIsFilterActive(false); // Hide Clear Filter button
    setCurrentPage(1);
  };

  // Export and download all data as CSV handler
  const handleDownloadCSV = () => {
    if (dataToPaginate.length === 0) {
      setSnackbarMessage("No data available to export.");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    const headers = [
      "Design Number",
      "Design Alias",
      "Item",
      "Design",
      "Size",
      "Collection",
      "Gross Weight",
      "Net Weight",
      "No of Days",
      "Tolerance/WT From",
      "Tolerance/WT To",
      "Design Date",
      "Karigar",
      "Status",
    ];

    const csvRows = [headers.join(",")];

    dataToPaginate.forEach((currentDesignData) => {
      const row = [
        currentDesignData?.tagno_alpha || "",
        currentDesignData?.certificateno || "",
        storeData.itemtypes?.find(
          (item) => item.id === currentDesignData.itemtype
        )?.name || "",
        storeData.designs?.find(
          (design) => design.id === currentDesignData.design
        )?.name || "",
        currentDesignData?.s_size_len || "",
        currentDesignData?.mfn_code || "",
        currentDesignData?.gross?.toFixed(3) + " gm" || "0.000 gm",
        currentDesignData?.netwt?.toFixed(3) + " gm" || "0.000 gm",
        currentDesignData?.pcs ?? "",
        currentDesignData?.tear_wt ?? "",
        currentDesignData?.pouch_tear_wt ?? "",
        currentDesignData?.entrydate
          ? new Date(currentDesignData.entrydate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "",
        storeData.karigars?.find(
          (karigar) => karigar.id === Number(currentDesignData.supplier)
        )?.name || "",
        currentDesignData?.status === "A" ? "Active" : "Inactive",
      ].map(
        (value) => `"${String(value).replace(/"/g, '""')}"` // Escape inner quotes for CSV compliance
      );

      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `designs_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // UI update after a design is deleted without reload
  const handleDesignDeleted = (deletedTagNo) => {
    // Update master data
    setExistingDesignsArr((prev) =>
      prev.filter((design) => design.tagno !== deletedTagNo)
    );

    // If filter is active, also update filtered designs
    if (isFilterActive) {
      setFilteredDesignsArr((prev) =>
        prev.filter((design) => design.tagno !== deletedTagNo)
      );
    }

    // Optional: reset pagination
    if ((currentPage - 1) * designsPerPage >= dataToPaginate.length - 1) {
      setCurrentPage(Math.max(1, currentPage - 1));
    }

    // Snackbar (optional)
    setSnackbarMessage("Design deleted successfully.");
    setSnackbarSeverity("success");
    setOpenSnackbar(true);
  };

  return (
    <div key={formKey}>
      {/* Design catalogue form */}
      <Paper
        elevation={2}
        sx={{ p: 3, borderRadius: 2, backgroundColor: "#F7F8F9", mb: 2 }}
      >
        {/* Design form */}
        <Grid container spacing={3}>
          {fields
            .filter(
              (field) =>
                ![
                  "toleranceFrom",
                  "toleranceTo",
                  "designDate",
                  "karigar",
                  "status",
                ].includes(field.key)
            )
            .map((field, index) => (
              <Grid item xs={12} md={4} key={index}>
                {/* For item field */}
                {field.key === "item" ? (
                  <Autocomplete
                    key={
                      designCatalogueData.item
                        ? designCatalogueData.item.id
                        : "item-reset"
                    }
                    disablePortal
                    id="item-select"
                    value={designCatalogueData.item}
                    onChange={(event, newValue) =>
                      setdesignCatalogueData((prev) => ({
                        ...prev,
                        item: newValue,
                      }))
                    }
                    options={storeData?.itemtypes || []}
                    getOptionLabel={(option) => option?.name || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                  />
                ) : // For design type field
                field.key === "design" ? (
                  <Autocomplete
                    key={
                      designCatalogueData.design
                        ? designCatalogueData.design.id
                        : "design-reset"
                    }
                    disablePortal
                    id="design-select"
                    value={designCatalogueData.design}
                    onChange={(event, newValue) =>
                      setdesignCatalogueData((prev) => ({
                        ...prev,
                        design: newValue,
                      }))
                    }
                    options={filteredDesigns}
                    getOptionLabel={(option) => option?.name || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Design"
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    disabled={!designCatalogueData.item}
                  />
                ) : // For all fields with text inputs
                field.type === "text" ? (
                  <TextField
                    label={field.label}
                    type="text"
                    value={designCatalogueData[field.key]}
                    onChange={handleChange(field.key)}
                    fullWidth
                    size="small"
                  />
                ) : // For all fields with number inputs
                field.type === "number" ? (
                  <TextField
                    label={field.label}
                    type="number"
                    value={designCatalogueData[field.key]}
                    onChange={handleDecimalChange(field.key, field.step || 1)}
                    fullWidth
                    size="small"
                    inputProps={{
                      step: field.step || 1,
                      min: 0,
                    }}
                  />
                ) : (
                  // For all fields with drop down
                  <FormControl fullWidth size="small">
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={designCatalogueData[field.key]}
                      onChange={handleChange(field.key)}
                    >
                      {(field.options || []).map((opt, idx) => (
                        <MenuItem key={idx} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>
            ))}

          {/* Row: Tolerance From, To & Date */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Tolerance from */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Tolerance/WT From"
                  value={designCatalogueData.toleranceFrom}
                  onChange={handleDecimalChange("toleranceFrom", 0.001)}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{
                    step: "0.001",
                    min: 0,
                  }}
                />
              </Grid>

              {/* Tolerance to */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Tolerance/WT To"
                  value={designCatalogueData.toleranceTo}
                  onChange={handleDecimalChange("toleranceTo", 0.001)}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{
                    step: "0.001",
                    min: 0,
                  }}
                />
              </Grid>

              {/* Design date */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Design Date"
                  type="date"
                  value={
                    designCatalogueData.designDate ||
                    new Date().toISOString().split("T")[0]
                  }
                  onChange={handleChange("designDate")}
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true, // Ensures the label stays above the date input
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Row: Karigar & Status */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Karigar */}
              <Grid item xs={12} md={4}>
                <Autocomplete
                  key={
                    designCatalogueData.karigar
                      ? designCatalogueData.karigar
                      : "karigar-reset"
                  }
                  disablePortal
                  id="karigar-select"
                  value={
                    karigars.find(
                      (karigar) =>
                        karigar.id === Number(designCatalogueData.karigar)
                    ) || null
                  }
                  onChange={(event, newValue) =>
                    setdesignCatalogueData((prev) => ({
                      ...prev,
                      karigar: newValue ? newValue.id : "",
                    }))
                  }
                  options={karigars}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Karigar"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={designCatalogueData.status}
                    onChange={handleChange("status")}
                  >
                    {fields
                      .find((f) => f.key === "status")
                      ?.options.map((opt, idx) => (
                        <MenuItem key={idx} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Empty column */}
              <Grid item xs={12} md={4} />
            </Grid>
          </Grid>

          {/* DesignDescription Field */}
          <Grid item xs={12}>
            <TextField
              label="Design Description"
              multiline
              rows={4}
              fullWidth
              value={designCatalogueData.designDescription}
              onChange={handleChange("designDescription")}
              size="small"
            />
          </Grid>

          {/* Files drop zone area and show uploaded files */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Image upload area */}
              <Grid item xs={12} md={4}>
                <div
                  {...getRootProps()}
                  className={`dropzone ${isDragActive ? "active" : ""}`}
                >
                  <input {...getInputProps()} />
                  <p>
                    <span className="upload-highlight">Click here</span> to
                    upload design images
                  </p>
                </div>
                <Typography variant="caption" color="textSecondary" mt={1}>
                  Maximum 4 images are allowed
                </Typography>
              </Grid>

              {/* Uploaded images preview area */}
              <Grid item xs={12} md={4}>
                {designCatalogueData.files.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    {designCatalogueData.files.map((file, index) => {
                      const isImage = file.type.startsWith("image");
                      return (
                        <Box
                          key={index}
                          sx={{
                            position: "relative",
                            width: 120,
                            height: 120,
                            borderRadius: 2,
                            overflow: "hidden",
                            boxShadow: 2,
                            border: "1px solid #ccc",
                          }}
                        >
                          {/* Cross icon */}
                          <Button
                            size="small"
                            onClick={() => handleRemoveFile(index)}
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              minWidth: "auto",
                              fontSize: 12,
                              backgroundColor: "#fff",
                              color: "black",
                              zIndex: 2,
                              lineHeight: 1,
                              borderRadius: "50%",
                            }}
                          >
                            X
                          </Button>

                          {isImage ? (
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`preview-${index}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              height="100%"
                              bgcolor="#eee"
                            >
                              <Typography
                                fontSize={12}
                                textAlign="center"
                                px={1}
                              >
                                {file.name}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={4} />
            </Grid>
          </Grid>

          {/* Row: Pagination dropdown, Create design, Filter, Clear Filter & Export CSV button */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Pagination dropdown */}
              <Grid item xs={12} md={4}>
                <FormControl
                  size="small"
                  variant="outlined"
                  fullWidth={true}
                  sx={{ maxWidth: { sm: 130 } }}
                >
                  <InputLabel id="page-count-label" shrink>
                    Select Page Count
                  </InputLabel>
                  <Select
                    labelId="page-count-label"
                    value={designsPerPage}
                    onChange={(e) => {
                      setDesignsPerPage(e.target.value);
                      setCurrentPage(1);
                    }}
                    label="Select Page Count"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Create design, Filter & Clear Filter buttons */}
              <Grid item xs={12} md={4}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={2}
                >
                  {/* Create Button */}
                  <Box display="flex" justifyContent="center">
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#0D7377",
                        color: "#fff",
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 4,
                        py: 1,
                        textTransform: "none",
                      }}
                      onClick={handleSubmit}
                      disabled={!isFormValid() || isCreating}
                    >
                      Create Design
                    </Button>
                  </Box>

                  {/* Filter button */}
                  <Box display="flex" justifyContent="center">
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#0D7377",
                        color: "#fff",
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 4,
                        py: 1,
                        textTransform: "none",
                      }}
                      onClick={handleFilter}
                      disabled={!isFilterEnabled() || isCreating}
                    >
                      Filter Design
                    </Button>
                  </Box>

                  {/* Clear filter button (show only if filter was applied) */}
                  {isFilterActive && (
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: "#0D7377",
                        color: "#0D7377",
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 4,
                        py: 1,
                        textTransform: "none",
                      }}
                      onClick={handleClearFilters}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </Box>
              </Grid>

              {/* Export CSV button */}
              <Grid item xs={12} md={4}>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#0D7377",
                      color: "#fff",
                      fontWeight: 600,
                      borderRadius: 2,
                      px: 4,
                      py: 1,
                      textTransform: "none",
                    }}
                    onClick={handleDownloadCSV}
                  >
                    Export CSV
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Design catalogue table */}
      <UniversalDataTable
        localStorageData={storeData}
        tableFor="Design Catalogue"
        currentPage={currentPage}
        designsPerPage={designsPerPage}
        setCurrentPage={setCurrentPage}
        paginatedDesignsArray={currentDesigns}
        totalDesignsCount={dataToPaginate.length}
        onDesignDeleted={handleDesignDeleted}
      />

      {/* Pagination buttons below the table */}
      {/* <Box mt={3} display="flex" justifyContent="center">
        <Pagination
          count={Math.ceil(dataToPaginate.length / designsPerPage)}
          page={currentPage}
          onChange={(event, value) => setCurrentPage(value)}
          color="primary"
          siblingCount={0}
          boundaryCount={1}
          size="small"
          disabled={dataToPaginate.length <= designsPerPage}
        />
        <Pagination
          count={Math.ceil(dataToPaginate.length / designsPerPage)}
          page={currentPage}
          onChange={(event, value) => setCurrentPage(value)}
          color="primary"
          siblingCount={0}
          boundaryCount={1}
          size="small"
          disabled={dataToPaginate.length <= designsPerPage}
          sx={{
            ml: "auto", // Push to the right side
            display: "flex",
            justifyContent: "flex-end",
            "& .MuiPaginationItem-root": {
              borderRadius: "4px", // Square border radius
              margin: "0 4px", // Space between pagination buttons
              padding: "6px 12px", // Adjust padding to look like square
            },
            "& .Mui-selected": {
              border: "2px solid #0D7377", // Add a border to the active (selected) item
              backgroundColor: "transparent", // Optional: to ensure background is not overridden
            },
            // backgroundColor: "yellow", // Optional, if you want a background color
            padding: "10px", // Padding around pagination
            border: "1px solid #D0D5DD", // Apply border color
          }}
        />
      </Box> */}

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000} // Auto close after 6 seconds
        onClose={() => setOpenSnackbar(false)} // Close the Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "100px !important", // force override snackbar position
          },
        }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Loading screen while creating new design */}
      <Backdrop open={isCreating} sx={{ zIndex: 2000, color: "#fff" }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Creating design...
          </Typography>
        </Box>
      </Backdrop>

      {/* Show success message after design creation */}
      <Dialog
        open={showSuccessBackdrop}
        onClose={() => setShowSuccessBackdrop(false)}
      >
        <DialogTitle>Design created successfully</DialogTitle>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            flexDirection: "column",
          }}
        >
          <Button
            onClick={() => {
              setShowSuccessBackdrop(false);
              // navigate("/createdesigncatalogue")
            }}
            sx={{ color: "green" }}
          >
            Create More
          </Button>
          <Button onClick={() => navigate("/home")}>Go Home</Button>
        </DialogActions>
      </Dialog>

      {/* Show error message for failed design creation */}
      <Dialog
        open={showErrorBackdrop}
        onClose={() => setShowErrorBackdrop(false)}
      >
        <DialogTitle>
          An unexpected error occured while creating design. Please try again
          later.
        </DialogTitle>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            flexDirection: "column",
          }}
        >
          <Button
            onClick={() => {
              setShowErrorBackdrop(false);
              // navigate("/createdesigncatalogue");
            }}
            sx={{ color: "blue" }}
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DesignCatalogue;
